import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { RealtimeEvents } from '@mc/shared/realtime';

import { ME_KEY, resetSession } from '../auth/session';

import { INVALIDATIONS } from './invalidations';

import type { InvalidatingEvent } from './invalidations';
import type { Socket } from 'socket.io-client';

/**
 * RF-GEN-08: con sesión y clínica activa, escucha los eventos de la clínica (salas por sucursal y
 * usuario, docs/05 §11) y vuelve a pedir lo que cambió. Se reconecta al cambiar de clínica (otras
 * salas). Si la sesión se cerró en otro lado, vuelve al ingreso al instante. El cliente de
 * Socket.IO se baja después de pintar: no va en el bundle inicial (RNF-PERF-05).
 */
export function useRealtime(userId: string | undefined, clinicId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (userId === undefined || clinicId == null) return;
    let socket: Socket | undefined;
    let closed = false;
    void import('socket.io-client').then(({ io }) => {
      if (closed) return;
      socket = io({ path: '/socket.io', transports: ['websocket'], withCredentials: true });
      subscribe(socket);
    });

    const subscribe = (socket: Socket) => {
      let revoked = false;
      let connectedBefore = false;

      // E correlaciona el evento con su payload dentro de la función (sin genérico haría falta un cast).
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
      const listen = <E extends InvalidatingEvent>(event: E) => {
        const name: string = event;
        socket.on(name, (payload: RealtimeEvents[E]) => {
          for (const queryKey of INVALIDATIONS[event](payload))
            void queryClient.invalidateQueries({ queryKey });
        });
      };
      for (const event of Object.keys(INVALIDATIONS) as InvalidatingEvent[]) listen(event);
      socket.on('session.revoked', () => {
        revoked = true;
        resetSession(queryClient, null);
      });
      socket.on('connect', () => {
        // Al reconectar pudo haber eventos perdidos: se vuelve a pedir todo lo visible.
        if (connectedBefore) void queryClient.invalidateQueries();
        connectedBefore = true;
      });
      socket.on('disconnect', (reason) => {
        // El servidor corta al cambiar de sesión o de clínica; si la sesión sigue, se vuelve a conectar.
        if (reason === 'io server disconnect' && !revoked) {
          void queryClient.refetchQueries({ queryKey: ME_KEY }).then(() => {
            if (queryClient.getQueryData(ME_KEY) != null) socket.connect();
          });
        }
      });
    };
    return () => {
      closed = true;
      socket?.disconnect();
    };
  }, [userId, clinicId, queryClient]);
}
